import { userLabel } from './user-identity.js';
/** ERP approval request/review UI. The server owns scope, permission and revision checks. */
import { showToast } from './calendar-history.js';

export function setupERPApproval(doc, { L, post, dirty, onNext }) {
    const card = doc.querySelector('#erpApproval');
    if (!card) return null;
    // A5: the header chip toggles the (now collapsible) card and lives outside
    // it, so it must stay clickable while everything else freezes — otherwise
    // a pending request locks its own way in.
    const chip = doc.querySelector('#erpApprovalChip');
    let current, busy = false, refreshSeq = 0;
    const disabled = new Map();
    const text = (tag, value, className) => { const el = document.createElement(tag); el.textContent = value || ''; if (className) el.className = className; return el; };
    const label = state => L['approval' + state.charAt(0).toUpperCase() + state.slice(1)] || state;
    function freeze(pending) {
        if (pending) doc.querySelectorAll('input, textarea, select, button').forEach(el => {
            if (card.contains(el) || el === chip || disabled.has(el)) return;
            disabled.set(el, el.disabled); el.disabled = true;
        });
        else { disabled.forEach((value, el) => { el.disabled = value; }); disabled.clear(); }
    }
    function render(data, updateVersion = false) {
        current = data;
        if (updateVersion) doc.dataset.version = String(data.version);
        // A5 fix round 1: the header chip is a separate element (outside the
        // card render() otherwise only touches) and must track the same
        // state — otherwise a same-page request/approve/reject/withdraw
        // leaves it showing the state from the initial server render until
        // a reload. Final fix wave: the chip can legitimately be absent
        // while the card still exists (policy off, no request yet — the
        // card renders hidden so the ⋯ menu's "승인 요청…" entry has
        // something to reveal), so `chip` may be null here; every use below
        // is already guarded.
        if (chip) { chip.className = 'erp-approval-chip erp-approval-state s-' + data.state; chip.textContent = label(data.state); }
        card.replaceChildren();
        const head = text('div', '', 'erp-approval-head');
        head.append(text('strong', L.approval), text('span', label(data.state), 'erp-approval-state s-' + data.state));
        const inbox = text('a', L.approvals); inbox.href = '/erp/approvals'; head.append(inbox); card.append(head);
        card.append(text('p', data.state === 'pending' ? L.approvalPendingHint : data.state === 'stale' ? L.approvalStale : data.required ? L.approvalRequiredHint : L.approvalOptionalHint, 'text-muted'));
        const form = document.createElement('form'); form.className = 'erp-approval-form';
        const note = document.createElement('textarea'); note.rows = 2; note.maxLength = 500; note.className = 'form-input'; note.placeholder = L.approvalNote; note.setAttribute('aria-label', L.approvalNote);
        let approver;
        if (data.can_request) {
            const field = document.createElement('label'); field.append(text('span', L.approvalApprover));
            approver = document.createElement('select'); approver.className = 'form-input'; approver.name = 'approver_id'; approver.required = true;
            approver.append(new Option(L.approvalChoose, ''));
            data.approvers.forEach(p => approver.append(new Option(userLabel(p), p.id)));
            field.append(approver); form.append(field);
            if (!data.approvers.length) form.append(text('p', L.approvalNoApprovers, 'form-hint'));
        }
        if (data.can_request || data.can_review) form.append(note);
        const actions = text('div', '', 'erp-approval-actions');
        const add = (action, title, primary) => {
            const button = text('button', title, 'btn btn-sm ' + (primary ? 'btn-primary' : 'btn-secondary'));
            button.type = 'button'; button.dataset.approvalAction = action;
            button.disabled = action === 'request' && !data.approvers.length;
            button.addEventListener('click', () => act(action, note, approver)); actions.append(button);
        };
        if (data.can_request) add('request', L.approvalRequest, true);
        if (data.can_review) { add('approve', L.approvalApprove, true); add('reject', L.approvalReject); }
        if (data.can_withdraw) add('withdraw', L.approvalWithdraw);
        form.append(actions); form.addEventListener('submit', e => e.preventDefault()); card.append(form);
        for (const row of data.history) {
            const details = document.createElement('details'); details.className = 'erp-approval-history';
            details.append(text('summary', label(row.state) + ' · ' + userLabel(row.requested_by) + ' → ' + userLabel(row.approver) + ' · ' + row.created_at));
            if (row.request_note) details.append(text('p', L.approvalRequestNote + ': ' + row.request_note));
            if (row.review_note) details.append(text('p', L.approvalReviewNote + ': ' + row.review_note));
            if (row.reviewed_at) details.append(text('p', L.approvalReviewedAt + ': ' + new Date(row.reviewed_at).toLocaleString()));
            const snapshot = row.snapshot;
            if (snapshot?.document) {
                const d = snapshot.document;
                details.append(text('strong', L.approvalSnapshot));
                const values = [d.number, d.title, d.bill_to_name, d.bill_to_biz_no, d.issue_date, d.due_date].filter(Boolean);
                details.append(text('p', values.join(' · ')));
                const table = document.createElement('table'); table.className = 'erp-approval-snapshot';
                const heading = document.createElement('tr');
                [L.approvalItem, L.approvalQty, L.approvalPrice, L.approvalLineTotal].forEach(value => heading.append(text('th', value)));
                table.append(heading);
                (snapshot.lines || []).forEach(line => { const tr = document.createElement('tr'); [line.name, line.qty, Number(line.unit_price).toLocaleString(), Number(line.amount).toLocaleString()].forEach(value => tr.append(text('td', String(value)))); table.append(tr); });
                details.append(table, text('p', L.approvalTotal + ': ' + Number(d.total).toLocaleString()));
                if (d.memo) details.append(text('p', d.memo));
            }
            card.append(details);
        }
        freeze(data.state === 'pending');
        // 다섯 모듈 UX §9.1: 결재가 게이트를 걸거나 풀면 머리의 주 버튼도 따라간다 (서버가 next_action 을 싣는다)
        if (onNext && data.next_action) onNext(data.next_action);
    }
    async function act(action, note, approver) {
        if (busy) return;
        if (dirty()) { showToast(L.approvalSaveFirst, { error: true }); return; }
        if (action === 'request' && !approver.value) { approver.reportValidity(); return; }
        if (action === 'reject' && !note.value.trim()) { showToast(L.approvalReasonRequired, { error: true }); note.focus(); return; }
        busy = true; refreshSeq += 1; card.querySelectorAll('button').forEach(b => { b.disabled = true; });
        try {
            render(await post('/api/erp/docs/' + doc.dataset.id + '/approval', {
                action, approval_id: current.current?.id || '', if_version: doc.dataset.version,
                approver_id: approver?.value || '', note: note.value.trim(),
            }, L), true);
            showToast(L.approvalUpdated);
        } catch (error) { showToast(error.message, { error: true }); await refresh(); }
        finally { busy = false; }
    }
    async function refresh() {
        const seq = ++refreshSeq;
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 15000);
        try {
            const r = await fetch('/api/erp/docs/' + doc.dataset.id + '/approval', { credentials: 'same-origin', headers: { Accept: 'application/json' }, signal: controller.signal });
            if (!r.ok) throw new Error(L.approvalLoadFailed);
            const data = await r.json(); if (card.isConnected && seq === refreshSeq) render(data);
        } catch (error) {
            if (!card.isConnected || seq !== refreshSeq) return;
            card.replaceChildren(text('p', L.approvalLoadFailed));
            const retry = text('button', L.approvalRetry, 'btn btn-secondary btn-sm'); retry.type = 'button'; retry.addEventListener('click', refresh); card.append(retry);
        } finally { clearTimeout(timeout); }
    }
    refresh();
    return { refresh };
}
